import type { ComponentType } from "react";
import type { Location } from "react-router";
import { CLASSIFIED_ADS_MENU_KEY } from "@/features/classified-ads/configs/classifiedAdsPaths.config";

type DrawerContentProps = {
  baseRoute: string
  location: Location
};

export type DrawerBehavior = {
  component: () => Promise<{ default: ComponentType<DrawerContentProps> }>
  persistenceKey: string
  hideBackButton?: boolean
  closeOnLinkClick?: boolean
};

export const DRAWER_BEHAVIORS: Record<string, DrawerBehavior> = {
  blog: {
    component: () =>
      import("@/features/navigation/components/DynamicBlogDrawerContent").then(
        (m) => ({ default: m.DynamicBlogDrawerContent }),
      ),
    persistenceKey: "blog",
  },
  boutique_yeati: {
    component: () =>
      import("@/features/boutique/components/BoutiqueDrawerContent").then(
        (m) => ({ default: m.BoutiqueDrawerContent }),
      ),
    persistenceKey: "boutique",
    hideBackButton: true,
    closeOnLinkClick: false,
  },
  bons_plans: {
    component: () =>
      import("@/features/deals/components/DealsDrawerContent").then(
        (m) => ({ default: m.DealsDrawerContent }),
      ),
    persistenceKey: "deals",
    closeOnLinkClick: false,
  },
  locations: {
    component: () =>
      import("@/features/rentals/components/RentalsDrawerContent").then(
        (m) => ({ default: m.RentalsDrawerContent }),
      ),
    persistenceKey: "catalogue",
    closeOnLinkClick: false,
    hideBackButton: true,
  },
  [CLASSIFIED_ADS_MENU_KEY]: {
    component: () =>
      import("@/features/classified-ads/components/ClassifiedAdsDrawerContent").then(
        (m) => ({ default: m.ClassifiedAdsDrawerContent }),
      ),
    persistenceKey: "classified-ads",
    closeOnLinkClick: false,
  },
};

export const MEGA_MENU_ENRICHMENT: Record<string, {
  featuredContent: {
    title: string
    description: string
    gradient: string
  }
  columns: 1 | 2 | 3
}> = {
  mon_cse: {
    featuredContent: {
      title: "Mon CSE",
      description: "Accédez aux services et informations de votre comité d'entreprise",
      gradient: "from-emerald-500 via-teal-500 to-cyan-600",
    },
    columns: 2,
  },
  mon_espace: {
    featuredContent: {
      title: "Mon espace",
      description: "Gérez votre profil, vos cartes et vos avantages",
      gradient: "from-pink-500 via-rose-500 to-red-500",
    },
    columns: 2,
  },
  boutique: {
    featuredContent: {
      title: "Boutique",
      description: "Profitez d'offres exclusives et de réductions sur une large gamme de produits et services",
      gradient: "from-orange-500 via-amber-500 to-yellow-500",
    },
    columns: 2,
  },
};

export const MEGA_MENU_CHILD_DESCRIPTIONS: Record<string, string> = {
  trombinoscope: "Découvrez l'annuaire des représentants",
  formulaires: "Accédez aux formulaires disponibles",
  blog: "Lisez les dernières actualités et annonces",
  echanges: "Participez aux échanges et débats de la communauté",
  base_documentaire: "Accédez à vos documents et fichiers partagés",
  calendrier: "Consultez les événements et dates importantes",
  sondages: "Donnez votre avis et consultez les résultats",
  enquetes: "Répondez aux enquêtes et consultez les résultats",
  tombolas: "Participez aux tombolas et concours",
  chat: "Discutez en temps réel avec vos collègues",
  remboursements: "Consultez vos demandes de remboursement",
  profil: "Gérez vos informations personnelles",
  carte_et_bons: "Consultez votre solde et gérez vos bons",
  avantages: "Découvrez vos avantages et subventions",
  boutique_yeati: "Découvrez tous nos produits et services",
  bons_plans: "Profitez des bons plans négociés par votre CSE",
  locations: "Réservez logements, salles, matériel ou livres du catalogue de votre CSE",
  mes_commandes: "Suivez vos commandes en cours et passées",
};

export const WARNING_KEYS = new Set(["profil"]);

export const BACK_HANDLER_KEYS = new Set(["blog", "boutique_yeati", "bons_plans"]);
